import { useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { HelpArticle } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import {
  emptyHelpArticleForm,
  fromHelpArticle,
  toHelpArticleCreate,
  toHelpArticleUpdate,
} from '../api/mappers';
import { ArticleStateBadge } from '../components/article-state-badge';
import { DeleteHelpArticleDialog } from '../components/delete-help-article-dialog';
import { HelpArticleFeedbackSection } from '../components/help-article-feedback';
import { HelpArticleForm } from '../components/help-article-form';
import { useHelpArticle } from '../hooks/use-help-article';
import { useCreateHelpArticle, useUpdateHelpArticle } from '../hooks/use-help-article-mutations';
import { useStoreLocales } from '../hooks/use-store-locales';
import { articleState } from '../schemas/article-state';

const EDIT_ROUTE = '/_authed/_active/s/$storeId/help/$articleId' as const;

function HelpArticleCreate() {
  const { t } = useTranslation('helpCenter');
  const store = useStore();
  const navigate = useNavigate();
  const create = useCreateHelpArticle(store.id);
  const locales = useStoreLocales(store.id, store.defaultLocale);

  return (
    <PageContent>
      <PageHeader
        title={t('create.title')}
        backTo={{ to: '/s/$storeId/help', params: { storeId: store.id } }}
        backLabel={t('articles.title')}
      />
      <HelpArticleForm
        storeId={store.id}
        mode="create"
        article={null}
        defaultValues={emptyHelpArticleForm()}
        locales={locales}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const created = await create.mutateAsync(toHelpArticleCreate(values));
          await navigate({
            to: '/s/$storeId/help/$articleId',
            params: { storeId: store.id, articleId: created.id },
            replace: true,
          });
        }}
      />
    </PageContent>
  );
}

function DangerZone({ storeId, row }: { storeId: string; row: HelpArticle }) {
  const { t } = useTranslation('helpCenter');
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<HelpArticle | null>(null);
  return (
    <FormSection title={t('danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('danger.description')}</p>
      <Button
        type="button"
        variant="outline"
        className="text-destructive hover:text-destructive w-full"
        onClick={() => setDeleting(row)}
      >
        <Trash2 aria-hidden />
        {t('actions.delete')}
      </Button>
      <DeleteHelpArticleDialog
        storeId={storeId}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={() => {
          // The record is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: '/s/$storeId/help', params: { storeId } });
        }}
      />
    </FormSection>
  );
}

function HelpArticleEdit() {
  const { t } = useTranslation('helpCenter');
  const format = useFormat();
  const store = useStore();
  const { articleId } = useParams({ from: EDIT_ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: article } = useHelpArticle(store.id, articleId);
  const update = useUpdateHelpArticle(store.id);
  const locales = useStoreLocales(store.id, store.defaultLocale);
  if (!article) return null;
  const defaults = fromHelpArticle(article);

  return (
    <PageContent>
      <PageHeader
        title={article.title}
        meta={<ArticleStateBadge state={articleState(article)} />}
        description={
          <span title={format.utc(article.updatedAt)}>
            {t('updatedAt', { when: format.relative(article.updatedAt) })}
            {' · '}
            <code>{article.path}</code>
          </span>
        }
        backTo={{ to: '/s/$storeId/help', params: { storeId: store.id } }}
        backLabel={t('articles.title')}
      />
      <HelpArticleForm
        key={article.updatedAt}
        storeId={store.id}
        mode="edit"
        article={article}
        defaultValues={defaults}
        locales={locales}
        isSubmitting={update.isPending}
        onSubmit={(values) =>
          update.mutateAsync({ id: article.id, body: toHelpArticleUpdate(values, defaults) })
        }
        dangerZone={
          <Can permission="help.write">
            <DangerZone storeId={store.id} row={article} />
          </Can>
        }
      >
        <HelpArticleFeedbackSection storeId={store.id} article={article} />
      </HelpArticleForm>
    </PageContent>
  );
}

export function HelpArticleCreatePage() {
  return (
    <FeatureErrorBoundary name="helpCenter.create">
      <HelpArticleCreate />
    </FeatureErrorBoundary>
  );
}

export function HelpArticleEditPage() {
  return (
    <FeatureErrorBoundary name="helpCenter.edit">
      <HelpArticleEdit />
    </FeatureErrorBoundary>
  );
}
